import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Box,
  Divider,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { EvidenceMetrics } from '../../services/api';
import { ConfidenceBadge } from './EvidenceDisplay';

interface EvidenceMetricsCardProps {
  metrics: EvidenceMetrics;
  compact?: boolean;
}

export function EvidenceMetricsCard({ metrics, compact = false }: EvidenceMetricsCardProps) {
  const totalRulesWithConf = 
    metrics.rulesWithHighConf + 
    metrics.rulesWithMediumConf + 
    metrics.rulesWithLowConf;

  if (compact) {
    // Compact view for scan list
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          {metrics.totalEvidence} evidence • {metrics.totalCapabilities} capabilities
        </Typography>
        <ConfidenceBadge confidence={metrics.avgConfidence} />
      </Box>
    );
  }

  // Full view for scan details
  return (
    <Card sx={{ mt: 2, border: '1px solid #E5E7EB' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <AssessmentIcon sx={{ color: '#6366F1' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Scan Evidence Summary
          </Typography>
          <ConfidenceBadge confidence={metrics.avgConfidence} />
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Aggregated evidence metrics across all scanned rules
        </Typography>

        {/* Quick Stats */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                Total Evidence
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {metrics.totalEvidence}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                Capabilities
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {metrics.totalCapabilities}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                Avg per Rule
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {metrics.avgEvidencePerRule.toFixed(1)}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                Confidence Range
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {Math.round(metrics.minConfidence * 100)}% - {Math.round(metrics.maxConfidence * 100)}%
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Confidence Distribution */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Rules by Confidence Level
          </Typography>

          <Stack spacing={1.5}>
            {metrics.rulesWithHighConf > 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{ fontSize: 18, color: '#10B981' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      HIGH (≥85%)
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#10B981' }}>
                    {metrics.rulesWithHighConf} rules
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(metrics.rulesWithHighConf / totalRulesWithConf) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#D1FAE5',
                    '& .MuiLinearProgress-bar': { 
                      backgroundColor: '#10B981',
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}

            {metrics.rulesWithMediumConf > 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WarningIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      MEDIUM (70-84%)
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#F59E0B' }}>
                    {metrics.rulesWithMediumConf} rules
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(metrics.rulesWithMediumConf / totalRulesWithConf) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#FEF3C7',
                    '& .MuiLinearProgress-bar': { 
                      backgroundColor: '#F59E0B',
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}

            {metrics.rulesWithLowConf > 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <InfoIcon sx={{ fontSize: 18, color: '#EF4444' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      LOW (&lt;70%)
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#EF4444' }}>
                    {metrics.rulesWithLowConf} rules
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(metrics.rulesWithLowConf / totalRulesWithConf) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#FEE2E2',
                    '& .MuiLinearProgress-bar': { 
                      backgroundColor: '#EF4444',
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}
          </Stack>
        </Box>

        {/* Capability Coverage */}
        {Object.keys(metrics.capabilitiesByLevel).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Capability Coverage
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {metrics.capabilitiesByLevel.STRONG !== undefined && metrics.capabilitiesByLevel.STRONG > 0 && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                    label={`${metrics.capabilitiesByLevel.STRONG} STRONG`}
                    size="small"
                    sx={{
                      backgroundColor: '#D1FAE5',
                      color: '#065F46',
                      border: '1px solid #10B981',
                      fontWeight: 600,
                    }}
                  />
                )}
                {metrics.capabilitiesByLevel.MEDIUM !== undefined && metrics.capabilitiesByLevel.MEDIUM > 0 && (
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: 16 }} />}
                    label={`${metrics.capabilitiesByLevel.MEDIUM} MEDIUM`}
                    size="small"
                    sx={{
                      backgroundColor: '#FEF3C7',
                      color: '#92400E',
                      border: '1px solid #F59E0B',
                      fontWeight: 600,
                    }}
                  />
                )}
                {metrics.capabilitiesByLevel.WEAK !== undefined && metrics.capabilitiesByLevel.WEAK > 0 && (
                  <Chip
                    icon={<InfoIcon sx={{ fontSize: 16 }} />}
                    label={`${metrics.capabilitiesByLevel.WEAK} WEAK`}
                    size="small"
                    sx={{
                      backgroundColor: '#FEE2E2',
                      color: '#991B1B',
                      border: '1px solid #EF4444',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>
            </Box>
          </>
        )}

        {/* Top Capabilities */}
        {metrics.topCapabilities && metrics.topCapabilities.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Detected Capabilities
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {metrics.topCapabilities.map((cap) => (
                  <Chip
                    key={cap}
                    label={cap}
                    size="small"
                    sx={{
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
